import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HiCheckCircle } from '../components/Icons';
import PublicNavbar from '../components/PublicNavbar';
import './DeleteAccount.css';

const DeleteAccount: React.FC = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      alert('Please enter your email address');
      return;
    }

    if (!confirmed) {
      alert('Please confirm that you understand the consequences of account deletion');
      return;
    }

    // Create mailto link with pre-filled content
    const subject = encodeURIComponent('Account Deletion Request');
    const body = encodeURIComponent(
      `I would like to request deletion of my account and all associated data.\n\n` +
      `Email: ${email}\n` +
      `Reason: ${reason || 'Not specified'}\n\n` +
      `I understand that:\n` +
      `- My account will be permanently deleted\n` +
      `- All my personal data will be removed\n` +
      `- This action cannot be undone\n` +
      `- I will lose access to all my orders, transactions, and services\n\n` +
      `Please proceed with the deletion of my account.`
    );

    const mailtoLink = `mailto:info@tappytray.com?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
    setSubmitted(true);
  };

  return (
    <div className="delete-account">
      <PublicNavbar>
        <Link to="/customer/login" className="nav-btn nav-btn-customer">Promoter Login</Link>
        <Link to="/admin/login" className="nav-btn nav-btn-admin">Admin Login</Link>
      </PublicNavbar>

      <main className="delete-content">
        <div className="container">
          <h1>Request Account Deletion</h1>
          <p className="intro-text">
            We're sorry to see you go. If you'd like to delete your account and all associated data, 
            please fill out the form below. We'll process your request within 30 days.
          </p>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="delete-form">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter the email address associated with your account"
                  required
                />
                <small>This must match the email address used to create your account</small>
              </div>

              <div className="form-group">
                <label htmlFor="reason">Reason for Deletion (Optional)</label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Help us improve by sharing why you're leaving (optional)"
                  rows={4}
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    required
                  />
                  <span>
                    I understand that deleting my account will permanently remove all my data, 
                    including orders, transactions, wallet balance, subscriptions, and service history. 
                    This action cannot be undone. *
                  </span>
                </label>
              </div>

              <div className="warning-box">
                <strong>⚠️ Important Information:</strong>
                <ul>
                  <li>Account deletion is permanent and cannot be reversed</li>
                  <li>All your personal data will be permanently removed</li>
                  <li>You will lose access to order history, transactions, and services</li>
                  <li>Any pending orders or subscriptions will be cancelled</li>
                  <li>We may retain certain information as required by law</li>
                </ul>
              </div>

              <button type="submit" className="submit-btn">
                Request Account Deletion
              </button>
            </form>
          ) : (
            <div className="success-message">
              <h2><HiCheckCircle size={28} /> Request Submitted</h2>
              <p>
                Your account deletion request has been prepared. Please check your email client 
                to send the request to us. If your email client didn't open automatically, 
                please send an email to <a href="mailto:info@tappytray.com">info@tappytray.com</a> 
                with the subject "Account Deletion Request".
              </p>
              <p>
                We will process your request within 30 days and send you a confirmation email 
                once your account has been deleted.
              </p>
              <Link to="/" className="back-link">Return to Home</Link>
            </div>
          )}

          <div className="alternative-method">
            <h3>Alternative Method</h3>
            <p>
              You can also send your deletion request directly via email to:{' '}
              <a href="mailto:info@tappytray.com?subject=Account%20Deletion%20Request">
                info@tappytray.com
              </a>
            </p>
            <p>
              Please include your account email address and confirm that you want to delete your account.
            </p>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} BoilingCode LLP. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
            <Link to="/refund-policy">Refund Policy</Link>
            <Link to="/delete-account">Delete Account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DeleteAccount;




